import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { setSelectedAccount, setAuthentication, setAvailableAccounts } from '../../store/appSlice';
import { endpoints } from '../../services/apiClient';
import { Account } from '../../types/schema';
import { track as trackAmplitude } from '../../services/amplitude';
import carbonSpaceLogo from '../../assets/logo.png';

const AccountSelectionContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const AccountSelectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: Number(theme.shape.borderRadius) * 3,
  boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
  maxWidth: 500,
  width: '100%',
  textAlign: 'center',
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: 80,
  marginBottom: theme.spacing(2),
}));

const AccountSelectionView: React.FC = () => {
  const dispatch = useDispatch();
  const { availableAccounts } = useSelector((state: RootState) => state.app);
  const [selectedAccountId, setSelectedAccountId] = useState<number | ''>('');
  const [error, setError] = useState('');
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);

  // Fetch accounts on component mount
  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoadingAccounts(true);
      try {
        const response = await endpoints.adminAccounts();
        const transformedAccounts = response.accounts.map(acc => ({
          accountId: acc.account_id,
          name: acc.name,
        }));
        dispatch(setAvailableAccounts(transformedAccounts));
      } catch (err) {
        console.error('Failed to fetch accounts:', err);
        setError('Failed to load accounts. Please refresh the page.');
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, [dispatch]);

  const handleAccountSelection = () => {
    if (!selectedAccountId) {
      setError('Please select an account to continue');
      return;
    }

    const selectedAccount = availableAccounts.find((acc: Account) => acc.accountId === selectedAccountId);
    if (selectedAccount) {
      dispatch(setSelectedAccount(selectedAccount));
      dispatch(setAuthentication(true));
      // Track account selection
      trackAmplitude('Account Selected', {
        account_id: selectedAccount.accountId,
        account_name: selectedAccount.name,
      });
    } else {
      setError('Selected account not found');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAccountSelection();
    }
  };

  if (isLoadingAccounts) {
    return (
      <AccountSelectionContainer maxWidth={false}>
        <AccountSelectionPaper>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>Loading accounts...</Typography>
          </Box>
        </AccountSelectionPaper>
      </AccountSelectionContainer>
    );
  }

  return (
    <AccountSelectionContainer maxWidth={false}>
      <AccountSelectionPaper>
        <LogoImage src={carbonSpaceLogo} alt="CarbonSpace Logo" />

        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Select Account
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          As an admin user, please select the account you want to work with
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>Select Account</InputLabel>
          <Select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value as number)}
            label="Select Account"
            onKeyPress={handleKeyPress}
          >
            {availableAccounts.map((account: Account) => (
              <MenuItem key={account.accountId} value={account.accountId}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 3, py: 1.5, textTransform: 'none' }}
          onClick={handleAccountSelection}
          disabled={!selectedAccountId}
        >
          Continue
        </Button>
      </AccountSelectionPaper>
    </AccountSelectionContainer>
  );
};

export default AccountSelectionView;
